<template>
  <q-btn
    round
    icon="more_vert"
    class="q-px-sm"
    :outline="canEdit && report.elementChangeCount > 0"
  >
    <q-badge
      v-if="canEdit && report.elementChangeCount > 0"
      color="warning"
      floating
      :label="report.elementChangeCount"
    />
    <q-menu
      bordered
      side="right"
      overlay
    >
      <q-list>
        <q-item
          v-close-popup
          clickable
          @click="report.runReport()"
        >
          <q-item-section avatar>
            <q-icon name="refresh" />
          </q-item-section>
          <q-item-section>
            Reload
          </q-item-section>
        </q-item>

        <q-item
          v-close-popup
          clickable
          :disable="!report.reportStatus.done"
          @click="report.email = currentUser.email; showEmailDialog = true"
        >
          <q-item-section avatar>
            <q-icon name="email" />
          </q-item-section>
          <q-item-section>
            Email
          </q-item-section>
          <q-tooltip v-if="!report.reportStatus.done">
            Option unavailable while report is running
          </q-tooltip>
        </q-item>

        <q-item
          v-close-popup
          clickable
          :disable="report.dirty || report.isLocal || !canCopyToClipboard"
          @click="copyPublicLink"
        >
          <q-item-section avatar>
            <q-icon name="link" />
          </q-item-section>
          <q-item-section>
            Copy Public Link
          </q-item-section>
          <q-tooltip v-if="!canCopyToClipboard">
            Copying to clipboard requires a secure connection (HTTPS)
          </q-tooltip>
        </q-item>

        <q-item
          v-close-popup
          clickable
          :disable="report.dirty || report.isLocal || !canCopyToClipboard"
          @click="copyPublicExportLink"
        >
          <q-item-section avatar>
            <q-icon name="link" />
          </q-item-section>
          <q-item-section>
            Copy Public Export Link
          </q-item-section>
          <q-tooltip v-if="!canCopyToClipboard">
            Copying to clipboard requires a secure connection (HTTPS)
          </q-tooltip>
        </q-item>

        <q-item
          v-close-popup
          clickable
          :disable="!report.reportStatus.done"
          @click="openPrintView()"
        >
          <q-item-section avatar>
            <q-icon name="print" />
          </q-item-section>
          <q-item-section>
            Print
          </q-item-section>
          <q-tooltip v-if="!report.reportStatus.done">
            Option unavailable while report is running
          </q-tooltip>
        </q-item>

        <q-item
          v-close-popup
          clickable
          :disable="!report.reportStatus.done"
          @click="saveAsPdf()"
        >
          <q-item-section avatar>
            <q-icon name="picture_as_pdf" />
          </q-item-section>
          <q-item-section>
            Save as PDF
          </q-item-section>
          <q-tooltip v-if="!report.reportStatus.done">
            Option unavailable while report is running
          </q-tooltip>
        </q-item>

        <!-- <q-item
          clickable
          @click="report.runReport()"
        >
          <q-item-section avatar>
            <q-icon name="image" />
          </q-item-section>
          <q-item-section>
            Save as Image
          </q-item-section>
        </q-item> -->

        <q-item
          v-close-popup
          clickable
          :disable="!report.reportStatus.done"
          @click="downloadCombinedCsv()"
        >
          <q-item-section avatar>
            <q-icon name="download" />
          </q-item-section>
          <q-item-section>
            Download Raw Data
          </q-item-section>
          <q-tooltip v-if="!report.reportStatus.done">
            Option unavailable while report is running
          </q-tooltip>
        </q-item>

        <q-separator />

        <q-item
          v-close-popup
          clickable
          @click="report.revalidate()"
        >
          <q-item-section avatar>
            <q-icon name="refresh" />
          </q-item-section>
          <q-item-section>
            Revalidate
          </q-item-section>
        </q-item>
        <q-item
          v-close-popup
          clickable
          @click="report.forceRecompute()"
        >
          <q-item-section avatar>
            <q-icon name="cached" />
          </q-item-section>
          <q-item-section>
            Force Recompute
          </q-item-section>
        </q-item>
        <q-item
          v-if="currentUser.superUser"
          v-close-popup
          clickable
          @click="report.forceRecomputeData()"
        >
          <q-item-section avatar>
            <q-icon name="warning" />
          </q-item-section>
          <q-item-section>
            Force Recompute Data
          </q-item-section>
          <q-tooltip>
            Use very carefully. This will force the report to recompute the data from scratch, which can take a very long time.
          </q-tooltip>
        </q-item>

        <template v-if="canEdit">
          <q-separator />
          <q-item
            v-close-popup
            clickable
          >
            <q-item-section>
              <q-toggle
                v-model="editing"
                :label="'Edit ' + report.scopeType + ' Report'"
                @update:model-value="val => $emit('edit', val)"
              />
            </q-item-section>
          </q-item>
        </template>
        <template v-if="canEdit && editing">
          <q-item
            v-if="editing"
            v-close-popup
            clickable
            @click="$emit('edit-report')"
          >
            <q-item-section avatar>
              <q-icon name="edit" />
            </q-item-section>
            <q-item-section>
              Report Setting
            </q-item-section>
          </q-item>
          <q-item
            v-if="editing && report.filterableColumns.length > 0"
            v-close-popup
            clickable
            @click="$emit('edit-filters')"
          >
            <q-item-section avatar>
              <q-icon name="filter_alt" />
            </q-item-section>
            <q-item-section>
              Edit Filters
            </q-item-section>
          </q-item>
          <q-item
            v-if="editing"
            v-close-popup
            clickable
            @click="$refs.printExportDialog.show(report)"
          >
            <q-item-section avatar>
              <q-icon name="print" />
            </q-item-section>
            <q-item-section>
              Print and Export Settings
            </q-item-section>
          </q-item>
          <q-item
            v-if="editing"
            v-close-popup
            clickable
            @click="$emit('add-element')"
          >
            <q-item-section avatar>
              <q-icon name="add" />
            </q-item-section>
            <q-item-section>
              New Element
            </q-item-section>
          </q-item>
          <q-item
            v-close-popup
            clickable
            :disable="copiedElementIds.length === 0"
            @click.stop="$emit('paste-elements')"
          >
            <q-item-section avatar>
              <q-icon name="content_paste" />
            </q-item-section>
            <q-item-section>
              Paste {{ copiedElementIds.length ? '(' + copiedElementIds.length + ' Elements)' : '' }}
            </q-item-section>
          </q-item>
        </template>
        <template v-if="canEdit && report.elementChangeCount > 0">
          <q-separator />
          <q-item
            v-if="!editing"
            clickable
            @click="editing = true; $emit('edit', true)"
          >
            <q-item-section avatar>
              <q-icon
                name="warning"
                color="warning"
              />
            </q-item-section>
            <q-item-section>
              Unsaved changes
            </q-item-section>
          </q-item>
          <q-item
            v-else
            v-close-popup
          >
            <q-item-section>
              <div class="row q-gutter-md">
                <q-btn
                  class="col"
                  color="negative"
                  size="sm"
                  label="Discard Changes"
                  @click="report.discardChanges()"
                />
                <q-btn
                  class="col"
                  color="positive"
                  size="sm"
                  label="Save Changes"
                  @click="report.publishChanges()"
                />
              </div>
            </q-item-section>
          </q-item>
        </template>
      </q-list>
    </q-menu>
    <q-dialog v-model="showEmailDialog">
      <q-card style="width: 400px;">
        <q-card-section class="row items-center q-mx-xs">
          <div class="text-h6">
            Email Report
          </div>
          <q-space />
          <q-btn
            v-close-popup
            icon="close"
            flat
            round
            dense
          />
        </q-card-section>
        <q-card-section>
          <div class="row q-gutter-col-md items-center">
            <q-input
              ref="email"
              v-model.trim="report.email"
              class="col-9"
              type="email"
              :label="$t('form.labels.emailAddress')"
              autofocus
              bottom-slots
              lazy-rules
              :rules="[
                val => required.$validator(val) || 'Field is required',
                val => email.$validator(val.toLowerCase()) || $t('form.errors.emailNotValid')
              ]"
            />
            <q-checkbox
              v-model="report.includeCsv"
              class="col-3 q-mb-md"
              label="Include CSV"
            >
              <q-tooltip>
                Include a CSV with raw reporting data in the emailed report for richer data inspection.
              </q-tooltip>
            </q-checkbox>
          </div>
        </q-card-section>

        <q-card-actions
          class="row justify-end q-ma-sm"
        >
          <q-btn
            label="Send Email"
            color="primary"
            :loading="sendingEmail"
            @click="sendEmail()"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <report-print-settings-dialog
      ref="printExportDialog"
      v-model="showPrintExportDialog"
      :report="report"
    />
  </q-btn>
</template>

<script>

import { email, required } from '@vuelidate/validators'
import { exportFile, Notify } from 'quasar'
import ReportPrintSettingsDialog from 'src/modules/admin/report/ReportPrintSettingsDialog'

export default {
  name: 'ReportContextMenu',
  components: {
    ReportPrintSettingsDialog
  },
  props: {
    report: {
      type: Object,
      required: true
    },
    copiedElementIds: {
      type: Array,
      required: false,
      default () {
        return []
      }
    }
  },
  emits: ['edit', 'edit-report', 'edit-filters', 'add-element', 'paste-elements'],
  data () {
    return {
      showEmailDialog: false,
      showPrintExportDialog: false,
      sendingEmail: false,
      editing: false
    }
  },
  context: ['currentUser'],
  configuration: ['currentSite'],
  computed: {
    canCopyToClipboard () {
      return window.isSecureContext
    },
    canEdit () {
      return this.report.user || (this.currentUser.admin && (!this.report.standard || this.currentSite.isLibrary))
    }
  },
  created () {
    _.extend(this, { email, required })
  },
  methods: {
    downloadCombinedCsv () {
      exportFile(
        `${this.report.fullTitle} - Combined.csv`,
        this.report.combinedCsv,
        'text/csv'
      )
    },
    async saveAsPdf () {
      // Make a copy so the save doesn't trigger a loading overlay in the view
      const report = this.report.clone()

      // API may retry PDF export several times, each time taking up to 10s.
      // Display a long enough timer to encourage user to wait long enough.
      const notifyDismiss = Notify.create({
        message: 'Creating PDF',
        timeout: 22000,
        progress: true,
        type: 'info',
        position: 'top'
      })

      const errors = await report.downloadPdf()
      notifyDismiss()

      if (errors && errors.length) return

      Notify.create({
        message: 'PDF Creation Complete',
        type: 'info',
        color: 'positive',
        position: 'top'
      })

      const a = document.createElement('a')
      a.href = 'data:application/pdf;base64,' + report.pdfData
      a.download = `${report.fullTitle}.pdf`
      a.click() // Downloaded file

      report.destroy()
    },
    async sendEmail () {
      this.showEmailDialog = false

      // Make a copy so the save doesn't trigger a loading overlay in the view
      const report = this.report.clone()

      const notifyDismiss = Notify.create({
        message: 'Building Email, Please Wait',
        timeout: 15000,
        progress: true,
        type: 'info',
        position: 'top'
      })

      const errors = await report.sendEmail()
      notifyDismiss()

      if (errors && errors.length) return

      Notify.create({
        message: 'Email Sent!',
        type: 'info',
        color: 'positive',
        position: 'top'
      })
    },
    openPrintView () {
      // We need to pull from window.location instead of $router because our updateRoute method
      // circumvents the $router and it doesn't have the current query parameters
      const url = '#/print_report/' + window.location.hash.split('/').splice(-1)[0] + '&print=true'
      window.open(url, '_blank')
    },
    copyPublicLink () {
      navigator.clipboard.writeText(this.report.publicReportUrl)

      Notify.create({
        message: 'Link copied to clipboard',
        type: 'info',
        position: 'top'
      })
    },
    copyPublicExportLink () {
      navigator.clipboard.writeText(this.report.publicReportExportUrl)

      Notify.create({
        message: 'Link copied to clipboard',
        type: 'info',
        position: 'top'
      })
    }
  }
}
</script>
